import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';

// -------------------------------------------------------------------------
// Application Insights singleton.
// Call initAppInsights() once from src/index.js before ReactDOM.render. Then
// import { trackCacheEvent } from anywhere in the app.
// -------------------------------------------------------------------------

export const reactPlugin = new ReactPlugin();

let _appInsights = null;

export function initAppInsights() {
    const connectionString =
        process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING;

    if (!connectionString) {
        if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.warn(
                '[AppInsights] REACT_APP_APPINSIGHTS_CONNECTION_STRING is not set – telemetry disabled.'
            );
        }
        return null;
    }

    const ai = new ApplicationInsights({
        config: {
            connectionString,
            extensions: [reactPlugin],
            enableAutoRouteTracking: true,
            disableFetchTracking: false,
            // CORS correlation and request/response header tracking are
            // disabled to avoid CORS preflight failures on cross-origin APIs
            // and to prevent sensitive header values (auth tokens, cookies)
            // from being logged to Application Insights.
            enableCorsCorrelation: false,
            enableRequestHeaderTracking: false,
            enableResponseHeaderTracking: false,
        },
    });

    ai.loadAppInsights();
    ai.trackPageView();
    _appInsights = ai;
    return ai;
}

/**
 * Track a cache hit or miss event. Safe to call even when App Insights is not
 * configured (no-op).
 *
 * @param {'cache_hit'|'cache_miss'} eventName
 * @param {string} url
 */
export function trackCacheEvent(eventName, url) {
    if (!_appInsights) return;
    try {
        _appInsights.trackEvent({ name: eventName }, { url });
    } catch (_e) {
        // never throw from telemetry
    }
}

// Test-only helper. Not exported through the package surface area in any
// meaningful way; useful for verifying init/no-init behaviour in unit tests.
export function _resetAppInsightsForTests() {
    _appInsights = null;
}

export default reactPlugin;
